import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, Fragment as _Fragment, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle, withModifiers as _withModifiers, Teleport as _Teleport } from "vue"

const _hoisted_1 = /*#__PURE__*/_createElementVNode("div", { id: "js-search-box-port" }, null, -1)
const _hoisted_2 = { class: "tw" }
const _hoisted_3 = ["onClick"]
const _hoisted_4 = { class: "relative" }
const _hoisted_5 = { class: "overflow-y-auto px-4 pb-4" }

import { ref, Ref, computed, watch, nextTick, onMounted, onBeforeUnmount, onUnmounted } from "vue";
import { useBreakpoints, onClickOutside } from "@vueuse/core";

import SvgIcon from "@SharedVueComponents/SvgIcon/SvgIcon.vue";
import TypeAhead from "@/components/TypeAhead.vue";
import SearchForm from "@/components/SearchForm.vue";
import DepartmentToggle from "@/components/DepartmentToggle.vue";
import CloseButton from "@SharedVueComponents/CloseButton/CloseButton.vue";
import EmarsysToast from "@/components/EmarsysToast.vue";
import SharedToast from "@/components/SharedToast.vue";

import { isClassicSite } from "@ocm/services/ocm-object";
import useSearch, { hasTypeAheadItems } from "@/composables/use-search";
import useFindings from "@/composables/use-findings";
import useMostSuggestedRequest from "@/composables/use-most-suggested-request";
import useScrollBehaviourBody from "@/composables/use-scroll-behaviour-body";

import OcmMessenger from "ocm_mercurius_messenger";
import { messengerPublicationTypes } from "@ocm/services/services.constants";
import { MessengerData } from "@/types/messenger.type";
import { animationDurationMs } from "@ocm/services/services.modal";


export default /*#__PURE__*/_defineComponent({
  __name: 'SearchApp',
  setup(__props) {

const isScreen1408px = useBreakpoints({ px1408: "88em" }).greaterOrEqual("px1408");
const { isLoading, userInput, isSearchVisible, isSearchMostSuggested, openSearch, closeSearch, triggerSearch } =
	useSearch();
const searchAppEl: Ref<HTMLElement | null> = ref(null);
onClickOutside(searchAppEl, () => isScreen1408px.value && closeSearch());

const { hasMostSuggestedItems } = useMostSuggestedRequest();
const { hasFindings } = useFindings();
const _hasResults = computed((): boolean => {
	if (isSearchMostSuggested.value) return hasMostSuggestedItems.value;
	if (isClassicSite) return hasTypeAheadItems.value;
	return hasFindings.value;
});
const showSearchBox = computed(() => !isScreen1408px.value || isLoading.value || _hasResults.value);

const searchBoxHeight: Ref<string> = ref("");
const _setSearchBoxHeight = () => {
	// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
	searchBoxHeight.value = `${window.visualViewport!.height}px`;
};

const _resetSearchBoxHeight = () => {
	window.visualViewport?.removeEventListener("resize", _setSearchBoxHeight);
	searchBoxHeight.value = "";
};

const timeout: Ref<number | undefined> = ref();
onBeforeUnmount(() => {
	clearTimeout(timeout.value);
});
const isDrawerOpen = ref(false);
const closeDrawer = () => {
	isDrawerOpen.value = false;
	timeout.value = setTimeout(closeSearch, animationDurationMs);
};

const { lockBody, unlockBody } = useScrollBehaviourBody();
watch(isSearchVisible, async (isSearchVisible) => {
	if (isSearchVisible) {
		await nextTick();
		if (!isScreen1408px.value) {
			isDrawerOpen.value = true;
			lockBody();
		}
		window.visualViewport?.addEventListener("resize", _setSearchBoxHeight);
	} else {
		isDrawerOpen.value = false;
		unlockBody();
		_resetSearchBoxHeight();
	}
});

watch(isScreen1408px, (isScreen1408px) => {
	if (!isSearchVisible.value) return;
	isDrawerOpen.value = !isScreen1408px;
	isScreen1408px ? unlockBody() : lockBody();
});

const onCloseSearch = () => {
	if (isScreen1408px.value) closeSearch();
};

const onTriggerSearch = ({ data }: MessengerData) => {
	if (typeof data !== "string") return;
	userInput.value = data;
	triggerSearch();
};

const isScreenSmallerThan1024px = useBreakpoints({ px1024: "64em" }).smaller("px1024");
const _skipLinkEl = document.querySelector("#js-skip-to-search");
const _updateSkipLink = () => {
	if (!_skipLinkEl) return;

	switch (true) {
		case isScreenSmallerThan1024px.value:
			_skipLinkEl.setAttribute("href", "#open-search-left-drawer");
			break;
		case isScreen1408px.value:
			_skipLinkEl.setAttribute("href", "#header-search");
			break;
		default:
			_skipLinkEl.setAttribute("href", "#open-search-right-drawer");
	}
};

onMounted(() => {
	OcmMessenger.subscribe(messengerPublicationTypes.OPEN_SEARCH, openSearch);
	OcmMessenger.subscribe(messengerPublicationTypes.CLOSE_SEARCH, onCloseSearch);

	if (document.querySelectorAll("[data-id=search]").length === 1) {
		OcmMessenger.subscribe(messengerPublicationTypes.TRIGGER_SEARCH, onTriggerSearch);
	}

	window.addEventListener("resize", _updateSkipLink);
	_updateSkipLink();
});

onUnmounted(() => {
	_resetSearchBoxHeight();
	window.removeEventListener("resize", _updateSkipLink);
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", {
      ref_key: "searchAppEl",
      ref: searchAppEl,
      class: "tw",
      "data-id": "search"
    }, [
      (!_unref(isScreen1408px))
        ? (_openBlock(), _createElementBlock("button", {
            key: 0,
            id: "open-search-right-drawer",
            class: "flex h-11 items-center justify-center px-3 lg:h-14",
            onClick: _cache[0] || (_cache[0] = ($event: any) => (_unref(openSearch)()))
          }, [
            _createVNode(SvgIcon, {
              class: "h-5 w-5",
              "icon-name": "search"
            })
          ]))
        : (_openBlock(), _createBlock(SearchForm, {
            key: 1,
            class: "px1408:w-80"
          })),
      _hoisted_1
    ], 512),
    (_openBlock(), _createBlock(_Teleport, {
      to: _unref(isScreen1408px) ? '#js-search-box-port' : 'body'
    }, [
      _createElementVNode("div", _hoisted_2, [
        (!_unref(isScreen1408px))
          ? (_openBlock(), _createElementBlock("div", {
              key: 0,
              class: _normalizeClass(["srh__drawer-overlay fixed left-0 top-0 z-[999] h-full w-screen justify-start lg:justify-end", [_unref(isSearchVisible) ? 'flex' : 'hidden', { 'bg-black/70': isDrawerOpen.value }]]),
              style: _normalizeStyle({ '--transition-duration': `${_unref(animationDurationMs)}ms` }),
              onClick: _withModifiers(closeDrawer, ["self","stop"])
            }, [
              _createElementVNode("div", {
                class: _normalizeClass(["srh__drawer o-scrollbar flex max-h-full w-[calc(var(--spacing-base-unit)*125)] max-w-full transform flex-col overflow-hidden overscroll-contain bg-white text-sm", { '-translate-x-full lg:translate-x-full': !isDrawerOpen.value }]),
                style: _normalizeStyle({ height: searchBoxHeight.value })
              }, [
                _createElementVNode("div", _hoisted_4, [
                  _createVNode(CloseButton, {
                    class: "right-4 top-2",
                    onClick: closeDrawer
                  }),
                  _createVNode(DepartmentToggle),
                  _createVNode(SearchForm, { class: "mx-4 mb-7 mt-8" })
                ]),
                _createElementVNode("div", _hoisted_5, [
                  (_unref(isLoading))
                    ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                        _createTextVNode("Loading …")
                      ], 64))
                    : (_openBlock(), _createBlock(TypeAhead, { key: 1 }))
                ])
              ], 6)
            ], 14, _hoisted_3))
          : (_openBlock(), _createElementBlock("div", {
              key: 1,
              class: _normalizeClass(["o-scrollbar absolute right-0 top-full z-[100] max-h-[calc(100vh-200px)] w-[calc(var(--spacing-base-unit)*125)] overscroll-contain border-t-2 border-t-gray-40 bg-white p-4 text-sm shadow-4", { hidden: !(_unref(isSearchVisible) && showSearchBox.value) }]),
              style: {"clip-path":"inset(0 -10px -10px -10px)"}
            }, [
              (_unref(isLoading))
                ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                    _createTextVNode("Loading …")
                  ], 64))
                : (_openBlock(), _createBlock(TypeAhead, { key: 1 }))
            ], 2))
      ])
    ], 8, ["to"])),
    _createVNode(EmarsysToast),
    _createVNode(SharedToast)
  ], 64))
}
}

})